/**
 * AR-2 Turn Classification Algorithm
 * Method ID: b1-turn-classification
 *
 * Classifies each turn independently as Skidding / Edging / Carving
 * using a 3-way scoring approach (carving / edging / skidding scores).
 *
 * 独立地将每个弯分类为搓雪 (Skidding) / 走刃 (Edging) / 卡宾 (Carving)，
 * 采用三路评分（卡宾分 / 走刃分 / 搓雪分），取最高分作为类型。
 *
 * Deterministic per PO-5.4 (no random numbers).
 * 按 PO-5.4 确定性实现（无随机数）。
 */

import { AR2_PARAMS } from '../constants/algorithm-params';
import { TurnType } from '../types/enums';
import type { SkiTickData } from '../types/ski-data';

// ===== Output Interface =====

/** 单个弯的分类结果 / Classification result for a single turn */
export interface TurnClassificationResult {
  turn_type: TurnType;
  confidence: number;          // 0-1
  carving_score: number;       // 0-100
  edging_score: number;        // 0-100
  skidding_score: number;      // 0-100
  mean_edge_angle: number;     // degrees
  edge_change_rate: number;    // degrees per sample point
  edge_sync: number;           // 0-100
  gyro_variance: number;       // (rad/s)²
}

// ===== Internal Threshold Constants =====

// Spec-specific thresholds not directly exposed in AR2_PARAMS.
// 规范指定、AR2_PARAMS 中未直接对应的阈值。
const EDGING_STABILITY_THRESHOLD = 4.5;    // degrees per point (edging stability)
const SKIDDING_GYRO_VARIANCE_THRESHOLD = 0.25; // (rad/s)² (skidding variance)

// ===== Helper Functions =====

/**
 * Compute the mean edge angle across turn frames.
 * Per-frame edge angle = (edge_angle_left + edge_angle_right) / 2,
 * averaged over all frames in [start, end] (inclusive).
 *
 * 计算弯内平均立刃角。每帧立刃角 = (左 + 右) / 2，对 [start, end]
 * （含端点）内所有帧取均值。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param start - start frame index (inclusive) / 起始帧索引（含）
 * @param end - end frame index (inclusive) / 结束帧索引（含）
 * @returns mean edge angle in degrees / 平均立刃角（度）
 */
export function computeMeanEdgeAngle(
  frames: SkiTickData[],
  start: number,
  end: number
): number {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);
  if (lo > hi) return 0;

  let sum = 0;
  let count = 0;
  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    sum += (f.edge_angle_left + f.edge_angle_right) / 2;
    count++;
  }
  return count > 0 ? sum / count : 0;
}

/**
 * Compute the edge change rate: mean absolute difference of the per-frame
 * mean edge angle between consecutive sample points.
 * per-frame edge angle = (edge_angle_left + edge_angle_right) / 2.
 * Returns 0 when fewer than 2 frames are available.
 *
 * 计算立刃变化率：相邻帧平均立刃角的平均绝对差。
 * 每帧立刃角 = (左 + 右) / 2。帧数不足 2 时返回 0。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param start - start frame index (inclusive) / 起始帧索引（含）
 * @param end - end frame index (inclusive) / 结束帧索引（含）
 * @returns mean abs diff in degrees per sample point / 平均绝对差（度/点）
 */
export function computeEdgeChangeRate(
  frames: SkiTickData[],
  start: number,
  end: number
): number {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);
  if (lo >= hi) return 0; // need at least 2 frames / 至少需要 2 帧

  let sumDiff = 0;
  let pairs = 0;
  let prevEdge =
    (frames[lo].edge_angle_left + frames[lo].edge_angle_right) / 2;

  for (let i = lo + 1; i <= hi; i++) {
    const f = frames[i];
    const currEdge = (f.edge_angle_left + f.edge_angle_right) / 2;
    sumDiff += Math.abs(currEdge - prevEdge);
    pairs++;
    prevEdge = currEdge;
  }
  return pairs > 0 ? sumDiff / pairs : 0;
}

/**
 * Compute edge sync: per-frame (100 - min(100, |edge_left - edge_right|)),
 * averaged across frames. 100 = perfect sync, 0 = max divergence.
 *
 * 计算立刃同步率：每帧 100 - min(100, |左 - 右|)，再对帧取均值。
 * 100 = 完全同步，0 = 差异最大。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param start - start frame index (inclusive) / 起始帧索引（含）
 * @param end - end frame index (inclusive) / 结束帧索引（含）
 * @returns edge sync score 0-100 / 立刃同步率 0-100
 */
export function computeEdgeSync(
  frames: SkiTickData[],
  start: number,
  end: number
): number {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);
  if (lo > hi) return 0;

  let sum = 0;
  let count = 0;
  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    const diff = Math.abs(f.edge_angle_left - f.edge_angle_right);
    sum += 100 - Math.min(100, diff);
    count++;
  }
  return count > 0 ? sum / count : 0;
}

/**
 * Compute the population variance of gyroscope Z values across turn frames.
 * Returns 0 when fewer than 2 frames are available.
 *
 * 计算弯内陀螺仪 Z 轴的总体方差。帧数不足 2 时返回 0。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param start - start frame index (inclusive) / 起始帧索引（含）
 * @param end - end frame index (inclusive) / 结束帧索引（含）
 * @returns population variance of gyro Z / 陀螺仪 Z 总体方差
 */
export function computeGyroVariance(
  frames: SkiTickData[],
  start: number,
  end: number
): number {
  const lo = Math.max(0, start);
  const hi = Math.min(frames.length - 1, end);
  const n = hi - lo + 1;
  if (n <= 1) return 0;

  // First pass: mean / 第一遍：均值
  let sum = 0;
  for (let i = lo; i <= hi; i++) {
    sum += frames[i].gyroscope.z;
  }
  const mean = sum / n;

  // Second pass: population variance / 第二遍：总体方差
  let sqSum = 0;
  for (let i = lo; i <= hi; i++) {
    const d = frames[i].gyroscope.z - mean;
    sqSum += d * d;
  }
  return sqSum / n;
}

// ===== Internal: Score Computation =====

/**
 * Carving score (0-100).
 *   edge_score       = 100 if mean_edge >= 25, else mean_edge / 25 * 100
 *   stability_score  = 100 if change_rate <= 3, else max(0, 100 - (rate-3)*10)
 *   sync_score       = edge_sync
 *   carving_score    = edge*0.4 + stability*0.3 + sync*0.3
 *
 * 卡宾分（0-100）。
 */
function computeCarvingScore(
  meanEdge: number,
  edgeChangeRate: number,
  edgeSync: number
): number {
  const carvingThresh = AR2_PARAMS.carvingThreshold; // 25 degrees
  const changeThresh = AR2_PARAMS.edgeChangeRateThreshold; // 3 deg/point

  // Edge score: full score when mean edge >= carving threshold.
  // 立刃分：平均立刃角 >= 卡宾阈值时满分。
  const edgeScore =
    meanEdge >= carvingThresh ? 100 : (meanEdge / carvingThresh) * 100;

  // Stability score: full score when change rate <= threshold.
  // 稳定性分：变化率 <= 阈值时满分。
  const stabilityScore =
    edgeChangeRate <= changeThresh
      ? 100
      : Math.max(0, 100 - (edgeChangeRate - changeThresh) * 10);

  // Sync score: directly from edge_sync (0-100).
  // 同步分：直接取 edge_sync（0-100）。
  const syncScore = edgeSync;

  return edgeScore * 0.4 + stabilityScore * 0.3 + syncScore * 0.3;
}

/**
 * Edging score (0-100).
 *   edge_score       = 100 if 15 <= mean_edge < 25
 *                      mean_edge / 15 * 100 if mean_edge < 15
 *                      max(0, 100 - (mean_edge - 25) * 5) if mean_edge >= 25
 *   stability_score  = 100 if change_rate <= 4.5,
 *                      else max(0, 100 - (rate - 4.5) * 8)
 *   sync_score       = edge_sync * 0.8
 *   edging_score     = edge*0.35 + stability*0.35 + sync*0.3
 *
 * 走刃分（0-100）。
 */
function computeEdgingScore(
  meanEdge: number,
  edgeChangeRate: number,
  edgeSync: number
): number {
  const edgingThresh = AR2_PARAMS.edgingThreshold; // 15 degrees
  const carvingThresh = AR2_PARAMS.carvingThreshold; // 25 degrees

  // Edge score: full in [15, 25); rises 0->100 below 15; decays above 25.
  // 立刃分：[15, 25) 满分；<15 线性上升；>=25 线性衰减。
  let edgeScore: number;
  if (meanEdge >= edgingThresh && meanEdge < carvingThresh) {
    edgeScore = 100;
  } else if (meanEdge < edgingThresh) {
    edgeScore = (meanEdge / edgingThresh) * 100;
  } else {
    edgeScore = Math.max(0, 100 - (meanEdge - carvingThresh) * 5);
  }

  // Stability score: threshold 4.5 deg/point.
  // 稳定性分：阈值 4.5 度/点。
  const stabilityScore =
    edgeChangeRate <= EDGING_STABILITY_THRESHOLD
      ? 100
      : Math.max(0, 100 - (edgeChangeRate - EDGING_STABILITY_THRESHOLD) * 8);

  // Sync score: scaled by 0.8.
  // 同步分：乘 0.8。
  const syncScore = edgeSync * 0.8;

  return edgeScore * 0.35 + stabilityScore * 0.35 + syncScore * 0.3;
}

/**
 * Skidding score (0-100).
 *   variance_score   = 100 if gyro_variance >= 0.25,
 *                      else gyro_variance / 0.25 * 100
 *   low_edge_score   = 100 if mean_edge < 15,
 *                      else max(0, 100 - mean_edge * 3)
 *   skidding_score   = variance*0.6 + low_edge*0.4
 *
 * 搓雪分（0-100）。
 */
function computeSkiddingScore(
  meanEdge: number,
  gyroVariance: number
): number {
  const edgingThresh = AR2_PARAMS.edgingThreshold; // 15 degrees

  // Variance score: full score when gyro variance >= threshold.
  // 方差分：陀螺仪方差 >= 阈值时满分。
  const varianceScore =
    gyroVariance >= SKIDDING_GYRO_VARIANCE_THRESHOLD
      ? 100
      : (gyroVariance / SKIDDING_GYRO_VARIANCE_THRESHOLD) * 100;

  // Low edge score: full score when mean edge < 15, decays above.
  // 低立刃分：平均立刃角 < 15 满分，否则衰减。
  const lowEdgeScore =
    meanEdge < edgingThresh ? 100 : Math.max(0, 100 - meanEdge * 3);

  return varianceScore * 0.6 + lowEdgeScore * 0.4;
}

// ===== Main Entry Points =====

/**
 * AR-2 Turn Classification: classify a single turn as Skidding/Edging/Carving.
 * Method ID: b1-turn-classification
 *
 * Algorithm steps / 算法步骤:
 * 1. Compute four signal metrics over [startFrameIndex, endFrameIndex]:
 *    mean edge angle, edge change rate, edge sync, gyro Z variance.
 *    计算 [start, end] 内四个信号指标：平均立刃角、立刃变化率、立刃同步率、陀螺仪 Z 方差。
 * 2. Compute three scores (carving / edging / skidding).
 *    计算三个分数（卡宾 / 走刃 / 搓雪）。
 * 3. turn_type = argmax of the three scores (ties favor Carving > Edging).
 *    turn_type = 三分数最大者（并列时优先卡宾 > 走刃）。
 * 4. confidence = max_score / (sum + 0.001), capped at 1.0.
 *    confidence = 最大分 / (总和 + 0.001)，上限 1.0。
 *
 * Frame range [startFrameIndex, endFrameIndex] is inclusive on both ends.
 * 帧范围 [startFrameIndex, endFrameIndex] 两端均含。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param startFrameIndex - start frame index (inclusive) / 起始帧索引（含）
 * @param endFrameIndex - end frame index (inclusive) / 结束帧索引（含）
 * @returns TurnClassificationResult / 单弯分类结果
 */
export function classifyTurn(
  frames: SkiTickData[],
  startFrameIndex: number,
  endFrameIndex: number
): TurnClassificationResult {
  // Step 1: signal metrics / 步骤 1：信号指标
  const meanEdge = computeMeanEdgeAngle(frames, startFrameIndex, endFrameIndex);
  const edgeChangeRate = computeEdgeChangeRate(
    frames,
    startFrameIndex,
    endFrameIndex
  );
  const edgeSync = computeEdgeSync(frames, startFrameIndex, endFrameIndex);
  const gyroVariance = computeGyroVariance(
    frames,
    startFrameIndex,
    endFrameIndex
  );

  // Step 2: three scores / 步骤 2：三个分数
  const carvingScore = computeCarvingScore(
    meanEdge,
    edgeChangeRate,
    edgeSync
  );
  const edgingScore = computeEdgingScore(
    meanEdge,
    edgeChangeRate,
    edgeSync
  );
  const skiddingScore = computeSkiddingScore(meanEdge, gyroVariance);

  // Step 3: pick the max (deterministic tie-break: Carving > Edging > Skidding)
  // 步骤 3：取最大值（确定性并列处理：卡宾 > 走刃 > 搓雪）
  const ranked: Array<{ type: TurnType; value: number }> = [
    { type: TurnType.Carving, value: carvingScore },
    { type: TurnType.Edging, value: edgingScore },
    { type: TurnType.Skidding, value: skiddingScore },
  ];
  let best = ranked[0];
  for (const item of ranked) {
    if (item.value > best.value) best = item;
  }

  // Step 4: confidence = max / (sum + eps), capped at 1.0
  // 步骤 4：置信度 = 最大分 / (总分 + eps)，上限 1.0
  const total = carvingScore + edgingScore + skiddingScore + 0.001;
  const confidence = Math.min(1.0, best.value / total);

  return {
    turn_type: best.type,
    confidence,
    carving_score: carvingScore,
    edging_score: edgingScore,
    skidding_score: skiddingScore,
    mean_edge_angle: meanEdge,
    edge_change_rate: edgeChangeRate,
    edge_sync: edgeSync,
    gyro_variance: gyroVariance,
  };
}

/**
 * Classify all turns given their frame boundaries.
 * 为所有弯按帧边界进行分类。
 *
 * @param frames - array of SkiTickData frames / 滑雪帧数据数组
 * @param turnBoundaries - per-turn { start_frame_index, end_frame_index }
 *                         每个弯的 { 起始帧, 结束帧 }
 * @returns classification result per turn (same order) / 每个弯的分类结果（同序）
 */
export function classifyAllTurns(
  frames: SkiTickData[],
  turnBoundaries: { start_frame_index: number; end_frame_index: number }[]
): TurnClassificationResult[] {
  return turnBoundaries.map((b) =>
    classifyTurn(frames, b.start_frame_index, b.end_frame_index)
  );
}

/**
 * Compute run-level score (0-100) from turn classifications.
 *   carving_ratio  = carving turns / total
 *   edging_ratio   = edging turns / total
 *   skidding_ratio = skidding turns / total
 *   score = carving_ratio * 100 + edging_ratio * 70 + (1 - skidding_ratio) * 30
 * capped at 100. Returns 0 when there are no turns.
 *
 * 根据各弯分类计算趟级评分（0-100）。
 *   卡宾比例 = 卡宾弯数 / 总弯数
 *   走刃比例 = 走刃弯数 / 总弯数
 *   搓雪比例 = 搓雪弯数 / 总弯数
 *   评分 = 卡宾比例 * 100 + 走刃比例 * 70 + (1 - 搓雪比例) * 30
 * 上限 100。无弯时返回 0。
 *
 * @param classifications - per-turn classification results / 各弯分类结果
 * @returns run-level score 0-100 / 趟级评分 0-100
 */
export function computeRunLevelScore(
  classifications: TurnClassificationResult[]
): number {
  const total = classifications.length;
  if (total === 0) return 0;

  let carvingCount = 0;
  let edgingCount = 0;
  let skiddingCount = 0;
  for (const c of classifications) {
    switch (c.turn_type) {
      case TurnType.Carving:
        carvingCount++;
        break;
      case TurnType.Edging:
        edgingCount++;
        break;
      case TurnType.Skidding:
        skiddingCount++;
        break;
    }
  }

  const carvingRatio = carvingCount / total;
  const edgingRatio = edgingCount / total;
  const skiddingRatio = skiddingCount / total;

  const score =
    carvingRatio * 100 + edgingRatio * 70 + (1 - skiddingRatio) * 30;

  // Clamp to [0, 100] / 限制在 [0, 100]
  return Math.max(0, Math.min(100, score));
}
